// client/src/pages/secure/ComplianceDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';

const statusStyles = {
    'Pending Review': 'bg-yellow-100 text-yellow-800',
    'Approved': 'bg-green-100 text-green-800',
    'Rejected': 'bg-red-100 text-red-800',
    'Deferred': 'bg-blue-100 text-blue-800',
};

const ComplianceDetailPage = () => {
    const { id } = useParams();
    const [document, setDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reviewStatus, setReviewStatus] = useState('');
    const [reviewNotes, setReviewNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const apiClient = useApiClient();

    const fetchDocument = useCallback(async () => {
        try {
            const data = await apiClient.get(`/api/compliance/records/${id}`);
            setDocument(data);
            setReviewStatus(data.review_status);
            setReviewNotes(data.notes || '');
        } catch (err) {
            setError(err.message || 'Failed to fetch document data.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient]);

    useEffect(() => {
        fetchDocument();
    }, [fetchDocument]);

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const updatedDocument = await apiClient.put(`/api/compliance/records/${id}/review`, {
                status: reviewStatus,
                notes: reviewNotes,
            });
            setDocument(updatedDocument);
            // Optionally show a success message
        } catch (err) {
            setError(err.message || 'Failed to save review.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="p-8">Loading document details...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!document) return <div className="p-8">Document not found.</div>;

    return (
        <div>
            <div className="mb-6">
                <Link to="/dashboard/compliance" className="text-indigo-600 hover:text-indigo-900">&larr; Back to Compliance List</Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Document Details Column */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white p-8 rounded-lg shadow">
                        <h1 className="text-3xl font-bold text-slate-800">{document.document_name}</h1>
                        <p className="text-lg text-slate-600">For transaction: {document.street_address}</p>
                        <div className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><p className="text-sm font-medium text-slate-500">Document Type</p><p className="text-lg text-slate-900">{document.document_type}</p></div>
                            <div><p className="text-sm font-medium text-slate-500">Uploaded By</p><p className="text-lg text-slate-900">{document.agent_first_name} {document.agent_last_name}</p></div>
                            <div><p className="text-sm font-medium text-slate-500">Upload Date</p><p className="text-lg text-slate-900">{new Date(document.upload_timestamp).toLocaleString()}</p></div>
                            <div>
                                <p className="text-sm font-medium text-slate-500">Current Status</p>
                                <p className={`text-lg font-semibold ${statusStyles[document.review_status] || 'bg-gray-100 text-gray-800'}`}>{document.review_status}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-8 rounded-lg shadow">
                         <h2 className="text-2xl font-bold text-slate-800 mb-4">Document Viewer</h2>
                         <div className="bg-gray-100 h-96 flex items-center justify-center rounded">
                             <p className="text-slate-500">[Document Preview Would Be Embedded Here]</p>
                         </div>
                    </div>
                </div>

                {/* Review Form Column */}
                <div className="bg-white p-8 rounded-lg shadow">
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Broker Review</h2>
                    <form onSubmit={handleReviewSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Review Action</label>
                            <div className="flex flex-wrap gap-2">
                                {['Approved', 'Rejected', 'Deferred'].map(status => (
                                    <button
                                        key={status}
                                        type="button"
                                        onClick={() => setReviewStatus(status)}
                                        className={`px-3 py-1 text-sm font-semibold rounded-full ${reviewStatus === status ? 'ring-2 ring-offset-1 ring-slate-500' : ''} ${statusStyles[status]}`}
                                    >
                                        {status}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label htmlFor="review_notes" className="block text-sm font-medium text-slate-700">Review Notes</label>
                            <textarea
                                id="review_notes"
                                value={reviewNotes}
                                onChange={(e) => setReviewNotes(e.target.value)}
                                rows="8"
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                                placeholder="Add notes for the agent, required for Rejected or Deferred status..."
                            ></textarea>
                        </div>
                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="w-full px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-400"
                            >
                                {isSaving ? 'Saving...' : 'Submit Review'}
                            </button>
                        </div>
                    </form>
                    {document.reviewed_by_broker_id && (
                        <div className="mt-6 border-t pt-4">
                            <p className="text-sm text-slate-500">
                                Last reviewed by {document.broker_first_name} {document.broker_last_name} on {new Date(document.review_timestamp).toLocaleString()}.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComplianceDetailPage;
